import React, { useState, useEffect, useCallback } from 'react';
import { getAndDecryptKvEntries } from '../services/kvDecryptionService';
import { getPrivateKeyFromSession } from '../services/cryptoKeyService';
import { formatAddress } from '../utils/address';
import { toUserFriendlyError } from '../utils/friendlyError';
import Modal from './Modal';
import './Modal.css';
import './KvDecryptionModal.css';

const KvDecryptionModal = ({ auditorIdentity, userAddress, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState(null);
  const [didRun, setDidRun] = useState(false);

  const handleDecrypt = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const sessionKey = getPrivateKeyFromSession();
      if (!sessionKey) {
        throw new Error('Session private key not found. Please log out and log in again.');
      }

      const decryptedEntries = await getAndDecryptKvEntries(
        auditorIdentity,
        userAddress,
        sessionKey
      );

      setEntries(decryptedEntries);
    } catch (err) {
      console.error('Decryption error:', err);
      setError(toUserFriendlyError(err, 'Could not decrypt these key entries. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [auditorIdentity, userAddress]);

  useEffect(() => {
    if (!didRun) {
      setDidRun(true);
      handleDecrypt();
    }
  }, [didRun, handleDecrypt]);

  return (
    <Modal
      title="Decrypt Key Request Entries"
      onClose={onClose}
      dismissible={!loading}
      nested
      wide
    >
      <div className="request-info">
        <div className="info-row">
          <span className="info-label">Auditor:</span>
          <span className="info-value">{formatAddress(auditorIdentity)}</span>
        </div>
        <div className="info-row">
          <span className="info-label">User Address:</span>
          <span className="info-value">{formatAddress(userAddress)}</span>
        </div>
      </div>

      {loading && (
        <div className="decryption-step">
          <div className="decrypting-state busy-state" role="status" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>Decrypting entries with your session key…</p>
          </div>
        </div>
      )}

      {!loading && error && !entries && (
        <div className="decryption-step">
          <div className="error-message" role="alert">{error}</div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="button-secondary">
              Cancel
            </button>
            <button type="button" onClick={handleDecrypt} className="button-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {!loading && entries && (
        <div className="decryption-results">
          <h3>Decryption Results</h3>
          <p className="step-description">
            Found {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}:
          </p>

          {entries.map((entry, index) => (
            <div
              key={entry.id || index}
              className={`entry-result ${entry.decryptionSuccess ? 'success' : 'error'}`}
            >
              <div className="entry-header">
                <h4>Entry {index + 1}</h4>
                {entry.decryptionSuccess ? (
                  <span className="status-badge success">Decrypted</span>
                ) : (
                  <span className="status-badge error">Failed</span>
                )}
              </div>

              <div className="entry-details">
                <div className="detail-row">
                  <span className="detail-label">Document ID:</span>
                  <span className="detail-value">{entry.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Key (k):</span>
                  <span className="detail-value code">{entry.k}</span>
                </div>
                {entry.decryptionSuccess ? (
                  <div className="detail-row">
                    <span className="detail-label">Decrypted Data:</span>
                    <div className="decrypted-content">
                      <pre className="decrypted-text">{entry.decrypted}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="detail-row">
                    <span className="detail-label">Error:</span>
                    <span className="detail-value error-text">{entry.decryptionError}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="modal-actions">
            <button
              type="button"
              onClick={() => {
                setEntries(null);
                setError(null);
                handleDecrypt();
              }}
              className="button-secondary"
            >
              Decrypt Again
            </button>
            <button type="button" onClick={onClose} className="button-primary">
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default KvDecryptionModal;
